import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Mail, CreditCard, Check, Sparkles } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Pills } from "./AiHtml";
import { useAuth } from "../context/AuthContext";
import { api, handleErr, waLink } from "../lib/api";

const loadCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) return resolve();
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = resolve;
    s.onerror = () => reject(new Error("Could not load Razorpay checkout"));
    document.body.appendChild(s);
  });

const isIndia = () => Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Calcutta" || Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Kolkata";
export const money = (n, cur) => (cur === "INR" ? `₹${n.toLocaleString("en-IN")}` : `$${n.toLocaleString("en-US")}`);
const PERKS = ["All 10 AI employees", "Every studio unlocked", "Unlimited brands", "Monthly PDF reports"];

export const BuyPlanDialog = () => {
  const { user, config, buyOpen, setBuyOpen, refresh, paywall } = useAuth();
  const [plan, setPlan] = useState("Agency");
  const [currency, setCurrency] = useState(isIndia() ? "INR" : "USD");
  const [billing, setBilling] = useState("monthly");
  const [busy, setBusy] = useState(false);
  if (!config) return null;
  const months = config.annualMonthsPaid;
  const price = (p) => (billing === "annual" ? p[currency.toLowerCase()] * months : p[currency.toLowerCase()]);
  const credits = (p) => (billing === "annual" ? p.credits * 12 : p.credits);

  const pay = async () => {
    setBusy(true);
    try {
      await loadCheckout();
      const { data: o } = await api.post("/payments/razorpay/order", { plan, currency, billing });
      const rz = new window.Razorpay({
        key: o.keyId, amount: o.amount, currency: o.currency, order_id: o.orderId,
        name: "NamoAI Digital", description: `${plan} ${billing} · ${o.credits.toLocaleString()} credits`,
        image: `${window.location.origin}/logo-mark.png`,
        prefill: { name: user?.name, email: user?.email }, theme: { color: "#FF6B00" },
        handler: async (res) => {
          try {
            await api.post("/payments/razorpay/verify", { orderId: res.razorpay_order_id, paymentId: res.razorpay_payment_id, signature: res.razorpay_signature });
            toast.success(`Welcome to ${plan}! ${o.credits.toLocaleString()} credits added.`);
          } catch (e) { handleErr(e); } finally { refresh(); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rz.on("payment.failed", (r) => toast.error(r.error?.description || "Payment failed"));
      setBuyOpen(false); // close Radix modal first: it sets body pointer-events:none + focus trap that block the Razorpay iframe
      rz.open();
    } catch (e) { handleErr(e); } finally { setBusy(false); }
  };

  return (
    <Dialog open={buyOpen} onOpenChange={setBuyOpen}>
      <DialogContent data-testid="buy-plan-dialog" className="max-w-4xl bg-[#0B1120] border-slate-800 max-h-[92vh] overflow-y-auto scroll-thin">
        <DialogHeader>
          {paywall ? (
            <div data-testid="paywall-header" className="space-y-2">
              <img src="/logo-mark.png" alt="" className="h-10" />
              <DialogTitle className="font-display text-2xl sm:text-3xl">Unlock the Power of AI!</DialogTitle>
              <p className="text-sm text-slate-400">Choose your plan to get started and experience smarter, faster and more efficient workflows with your 10-member AI marketing team.</p>
            </div>
          ) : <DialogTitle className="font-display text-2xl">Choose your plan</DialogTitle>}
        </DialogHeader>
        <div className="flex flex-wrap gap-3 justify-between">
          <Pills testPrefix="billing" value={billing} onChange={setBilling} options={[{ value: "monthly", label: "Monthly" }, { value: "annual", label: `Annual · ${12 - months} months free` }]} />
          <Pills testPrefix="currency" value={currency} onChange={setCurrency} options={[{ value: "INR", label: "₹ INR" }, { value: "USD", label: "$ USD" }]} />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {Object.entries(config.plans).map(([name, p]) => (
            <button key={name} data-testid={`plan-${name.toLowerCase()}`} onClick={() => setPlan(name)}
              className={`relative text-left p-4 rounded-xl border transition-colors ${plan === name ? "border-orange-500 bg-orange-500/10" : "border-slate-800 hover:border-slate-600"}`}>
              {name === "Agency" && <span className="absolute -top-2.5 right-3 text-[10px] px-2 py-0.5 rounded-full bg-orange-500 text-white font-semibold">POPULAR</span>}
              <p className="label-mono">{name}</p>
              <p className="text-2xl font-bold font-display mt-2" data-testid={`plan-${name.toLowerCase()}-price`}>{money(price(p), currency)}</p>
              <p className="text-xs text-slate-500">{billing === "annual" ? "per year" : "per month"}</p>
              <p className="text-sm text-orange-300 mt-2">{credits(p).toLocaleString()} credits</p>
              <p className="text-xs text-slate-400 mt-1">{p.audience}</p>
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-slate-300">
          {PERKS.map((x) => <span key={x} className="flex items-center gap-1.5"><Check size={13} className="text-emerald-400" />{x}</span>)}
        </div>
        {config.razorpayEnabled ? (
          <Button data-testid="razorpay-pay-btn" disabled={busy} onClick={pay} className="w-full rounded-full h-11">
            {paywall ? <Sparkles size={16} className="mr-2" /> : <CreditCard size={16} className="mr-2" />}
            {busy ? "Opening checkout..." : `Get ${plan} · ${money(price(config.plans[plan]), currency)}`}
          </Button>
        ) : (
          <p data-testid="razorpay-not-configured" className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">Online payments are not configured yet. Contact us on WhatsApp.</p>
        )}
        <p className="text-xs text-slate-500">Secure payment by Razorpay. Credits are added instantly. {currency === "USD" && "International cards supported."}</p>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <a data-testid="whatsapp-link" href={waLink(`Hi, I want the ${plan} plan`)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-slate-300 hover:text-emerald-400"><MessageCircle size={16} className="text-emerald-400" /> {config.whatsapp}</a>
          <span className="flex items-center gap-2 text-slate-400"><Mail size={16} className="text-blue-400" /> {config.supportEmail}</span>
        </div>
      </DialogContent>
    </Dialog>
  );
};
